import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Multi-select built on @radix-ui/react-dropdown-menu, the sibling of
 * <SelectMenu> — same reason for not using the shadcn <Select> (wheel-scroll
 * bug), same trigger styling.
 *
 * @param {{
 *   value?: string[],
 *   onChange?: (value: string[]) => void,
 *   options: Array<{ value: string, label: React.ReactNode, hint?: string, search?: string }>,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   id?: string,
 *   searchable?: boolean,
 *   searchPlaceholder?: string,
 *   triggerClassName?: string,
 *   contentClassName?: string,
 *   emptyLabel?: string,
 * }} props
 */
const MultiSelectMenu = React.forwardRef(function MultiSelectMenu(
  {
    value = [],
    onChange,
    options,
    placeholder = 'Select...',
    disabled,
    id,
    searchable = false,
    searchPlaceholder = 'Filter…',
    triggerClassName,
    contentClassName,
    emptyLabel = 'No options',
  },
  ref,
) {
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      (o.search ?? `${o.value} ${o.hint ?? ''}`).toLowerCase().includes(q),
    );
  }, [options, query]);

  const labelByValue = React.useMemo(
    () => Object.fromEntries(options.map(o => [o.value, o.label])),
    [options],
  );

  const toggle = optionValue =>
    onChange?.(
      value.includes(optionValue) ? value.filter(v => v !== optionValue) : [...value, optionValue],
    );

  return (
    <DropdownMenu onOpenChange={open => !open && setQuery('')}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          ref={ref}
          id={id}
          type="button"
          className={cn(
            'flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName,
          )}>
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <span className="flex min-w-0 flex-wrap gap-1">
              {value.map(v => (
                <Badge key={v} variant="secondary" className="font-normal">
                  {labelByValue[v] ?? v}
                  <X
                    className="ml-1 h-3 w-3 opacity-60 hover:opacity-100"
                    role="button"
                    aria-label={`Remove ${v}`}
                    // Radix opens the menu on pointerdown, so stopping the click
                    // alone would remove the chip *and* open the dropdown.
                    onPointerDown={e => e.stopPropagation()}
                    onClick={e => {
                      e.stopPropagation();
                      onChange?.(value.filter(item => item !== v));
                    }}
                  />
                </Badge>
              ))}
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          'max-h-80 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto',
          contentClassName,
        )}
        align="start">
        {searchable && (
          <>
            <div className="p-1">
              <Input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8"
                // Radix's menu typeahead steals keystrokes (and focus) from an
                // input rendered inside the content — keep them local.
                onKeyDown={e => e.stopPropagation()}
              />
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {filtered.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyLabel}</div>
        ) : (
          filtered.map(opt => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={value.includes(opt.value)}
              // Keep the menu open so several options can be picked in one go.
              onSelect={e => e.preventDefault()}
              onCheckedChange={() => toggle(opt.value)}>
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{opt.label}</span>
                {opt.hint && (
                  <span className="truncate text-xs text-muted-foreground">{opt.hint}</span>
                )}
              </span>
            </DropdownMenuCheckboxItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export { MultiSelectMenu };
